import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, message, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getImportJob,
  createImportJob,
  updateImportJob,
  ImportFileFormats,
  ImportDuplicateStrategies,
  DataManagementModules,
} from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';

export default function ImportJobForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!id;
  const canWrite = hasPermission('data_management', 'write');

  useEffect(() => {
    if (!canWrite) navigate('/data-management/imports');
  }, [canWrite, navigate]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getImportJob(id);
        if (res.data?.success && res.data.data) {
          form.setFieldsValue(res.data.data);
        }
      } catch {
        message.error(t('app.error'));
      }
    })();
  }, [id, form, t]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updateImportJob(id!, values);
        message.success(t('data_management.import.update_success'));
      } else {
        await createImportJob(values);
        message.success(t('data_management.import.create_success'));
      }
      navigate('/data-management/imports');
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ file_format: 'csv', duplicate_strategy: 'skip' }}
      >
        <Form.Item
          name="name"
          label={t('data_management.import.field.name')}
          rules={[{ required: true, max: 200 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('data_management.import.field.description')}
          rules={[{ max: 1000 }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            name="target_module"
            label={t('data_management.import.field.target_module')}
            rules={[{ required: true }]}
          >
            <Select
              options={DataManagementModules.map((m) => ({
                value: m,
                label: t(`data_management.import.modules.${m}`),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="file_format"
          label={t('data_management.import.field.file_format')}
          rules={[{ required: true }]}
        >
          <Select
            disabled={isEdit}
            options={ImportFileFormats.map((f) => ({
              value: f,
              label: t(`data_management.import.formats.${f}`),
            }))}
          />
        </Form.Item>

        {!isEdit && (
          <>
            <Form.Item
              name="file_name"
              label={t('data_management.import.field.file_name')}
              rules={[{ required: true, max: 500 }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="file_size"
              label={t('data_management.import.field.file_size')}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="file_id"
              label={t('data_management.import.field.file_id')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="duplicate_strategy"
          label={t('data_management.import.field.duplicate_strategy')}
        >
          <Select
            options={ImportDuplicateStrategies.map((s) => ({
              value: s,
              label: t(`data_management.import.duplicate_strategies.${s}`),
            }))}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? t('app.save') : t('app.create')}
            </Button>
            <Button onClick={() => navigate('/data-management/imports')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
